import { useQuery } from '@tanstack/react-query'
import {
  Brain,
  HeartHandshake,
  Layers3,
  MessageSquareText,
  Phone,
  Sparkles,
  Target,
  TrendingUp
} from 'lucide-react'
import { Sidebar } from '../components/Sidebar'
import {
  buildManagerFeedbackOverview,
  type FeedbackDataCoverage,
  type ManagerFeedbackCall,
  type ManagerFeedbackProfile,
  type ManagerFeedbackSourceSummary,
  type ManagerFeedbackTraitScore
} from '../lib/feedbackProfile'
import { hasInternalApi, internalApiGet } from '../lib/internalApi'
import { supabase } from '../lib/supabase'
import type { SavedLineImport } from '../lib/useLineImports'
import { useMyManager } from '../lib/useManagerProfile'

type CallsResponse = {
  calls: ManagerFeedbackCall[]
}

type LineImportsResponse = {
  line_imports: SavedLineImport[]
}

export function FeedbackPage() {
  const manager = useMyManager()
  const calls = useQuery<ManagerFeedbackCall[]>({
    queryKey: ['manager-feedback', 'calls'],
    queryFn: async () => {
      if (hasInternalApi()) {
        const data = await internalApiGet<CallsResponse>('/api/calls?limit=1000')
        return data.calls
      }

      const { data, error } = await supabase
        .from('call_records')
        .select('id,liver_id,called_at,duration_seconds,status,todos,next_follow_date,summary,transcript,error_message,updated_at')
        .order('called_at', { ascending: false })
        .limit(1000)
      if (error) throw error
      return (data ?? []) as ManagerFeedbackCall[]
    }
  })
  const lineImports = useQuery<SavedLineImport[]>({
    queryKey: ['manager-feedback', 'line-imports'],
    queryFn: async () => {
      if (hasInternalApi()) {
        const data = await internalApiGet<LineImportsResponse>('/api/line-imports?limit=1000')
        return data.line_imports
      }

      const { data, error } = await supabase
        .from('line_imports')
        .select('id,liver_id,uploaded_by,file_name,source_hash,message_count,active_days,date_start,date_end,primary_manager_name,analysis,handoff_note,created_at,updated_at')
        .order('created_at', { ascending: false })
        .limit(1000)
      if (error) throw error
      return (data ?? []) as SavedLineImport[]
    }
  })
  const overview = buildManagerFeedbackOverview(calls.data ?? [], lineImports.data ?? [], new Date())
  const profile = overview.overallProfile
  const loading = calls.isLoading || lineImports.isLoading || manager.isLoading
  const error = calls.error ?? lineImports.error ?? manager.error
  const managerName = manager.data?.name?.trim() || manager.data?.email || '現在のマネージャー'

  return (
    <div className="flex min-h-full">
      <Sidebar />
      <main className="min-w-0 flex-1 bg-gray-50 px-4 py-6 pt-16 sm:px-6 md:pt-8">
        <div className="mx-auto max-w-6xl space-y-6">
          <header className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">フィードバック</h1>
              <p className="mt-1 text-sm leading-6 text-gray-500">
                {managerName} の電話とLINE履歴を分けて見ながら、総合的なマネジメント傾向を集計します。
              </p>
            </div>
            {loading && <p className="text-xs text-gray-400">分析中...</p>}
            {error && (
              <p className="text-xs text-red-600">
                フィードバック取得に失敗: {error instanceof Error ? error.message : String(error)}
              </p>
            )}
          </header>

          <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <FeedbackMetric
              icon={<Sparkles className="h-4 w-4" />}
              label="総合スコア"
              value={`${profile.overallScore}`}
              note={`${coverageLabel(profile.dataCoverage)} / 電話+LINE`}
              tone="sky"
            />
            <FeedbackMetric
              icon={<Phone className="h-4 w-4" />}
              label="電話フィードバック"
              value={`${overview.callProfile.overallScore}`}
              note={`分析 ${overview.callProfile.analyzedCalls} / 通話 ${overview.callProfile.totalCalls}`}
              tone="emerald"
            />
            <FeedbackMetric
              icon={<MessageSquareText className="h-4 w-4" />}
              label="LINEフィードバック"
              value={`${overview.lineProfile.overallScore}`}
              note={`分析 ${overview.lineProfile.analyzedCalls} / 履歴 ${overview.lineProfile.totalCalls}`}
              tone="violet"
            />
            <FeedbackMetric
              icon={<TrendingUp className="h-4 w-4" />}
              label="根拠シグナル"
              value={`${profile.evidenceCount}件`}
              note={profile.primaryStrengths[0]?.label ?? 'データ待ち'}
              tone="amber"
            />
          </section>

          <SourceComparisonCard summaries={overview.sourceSummaries} />

          <section className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
            <TraitScoreCard traits={profile.traits} />
            <InsightCard
              strengths={profile.primaryStrengths}
              improvements={profile.improvementAreas}
              callProfile={overview.callProfile}
              lineProfile={overview.lineProfile}
            />
          </section>

          <section className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">根拠の見え方</h2>
                <p className="mt-1 text-xs text-gray-500">
                  AI評価を入れる前段として、現在は既存ログから説明可能なシグナルだけを表示しています。
                </p>
              </div>
              <Brain className="h-4 w-4 text-gray-400" />
            </div>
            {profile.traits.some((trait) => trait.evidence.length > 0) ? (
              <div className="grid gap-3 md:grid-cols-2">
                {profile.traits
                  .filter((trait) => trait.evidence.length > 0)
                  .slice(0, 6)
                  .map((trait) => (
                    <div key={trait.key} className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-medium text-gray-900">{trait.label}</p>
                        <span className="text-xs tabular-nums text-gray-500">{trait.score}</span>
                      </div>
                      <ul className="mt-2 space-y-1">
                        {trait.evidence.slice(0, 2).map((evidence) => (
                          <li key={evidence} className="line-clamp-2 text-xs leading-5 text-gray-600">
                            {evidence}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
              </div>
            ) : (
              <p className="rounded-lg bg-gray-50 px-4 py-6 text-sm text-gray-400">
                分析できる通話ログがまだありません。
              </p>
            )}
          </section>
        </div>
      </main>
    </div>
  )
}

type MetricTone = 'sky' | 'emerald' | 'violet' | 'amber'

function FeedbackMetric({
  icon,
  label,
  value,
  note,
  tone
}: {
  icon: React.ReactNode
  label: string
  value: string
  note: string
  tone: MetricTone
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-medium text-gray-500">{label}</p>
        <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${TONE_CLASS[tone]}`}>
          {icon}
        </span>
      </div>
      <p className="mt-4 text-2xl font-semibold tabular-nums text-gray-900">{value}</p>
      <p className="mt-1 truncate text-xs text-gray-500">{note}</p>
    </div>
  )
}

function TraitScoreCard({ traits }: { traits: ManagerFeedbackTraitScore[] }) {
  const maxScore = Math.max(1, ...traits.map((trait) => trait.score))

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">マネジメント軸</h2>
          <p className="mt-1 text-xs text-gray-500">通話ログから見える支援スタイルの偏りです。</p>
        </div>
        <Target className="h-4 w-4 text-gray-400" />
      </div>
      <div className="space-y-3">
        {traits.map((trait) => (
          <div key={trait.key} className="grid gap-2 sm:grid-cols-[136px_minmax(0,1fr)_44px] sm:items-center">
            <div>
              <p className="text-xs font-medium text-gray-700">{trait.label}</p>
              <p className="mt-0.5 text-[11px] text-gray-400">信頼度 {Math.round(trait.confidence * 100)}%</p>
            </div>
            <div className="h-3 rounded-full bg-gray-100">
              <div
                className="h-3 rounded-full bg-sky-400"
                style={{ width: `${Math.max(4, (trait.score / maxScore) * 100)}%` }}
              />
            </div>
            <p className="text-right text-xs font-semibold tabular-nums text-gray-700">{trait.score}</p>
          </div>
        ))}
      </div>
    </section>
  )
}

function SourceComparisonCard({ summaries }: { summaries: ManagerFeedbackSourceSummary[] }) {
  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">分析ソース別</h2>
          <p className="mt-1 text-xs text-gray-500">
            電話は深い相談、LINEは日常接点として分けて読み、最後に総合判断へ寄せます。
          </p>
        </div>
        <Layers3 className="h-4 w-4 text-gray-400" />
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        {summaries.map((summary) => (
          <div key={summary.source} className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-semibold text-gray-900">{summary.label}</p>
              <span className="rounded-full bg-white px-2 py-0.5 text-xs tabular-nums text-gray-600 ring-1 ring-gray-200">
                {summary.score}
              </span>
            </div>
            <div className="mt-3 h-2 rounded-full bg-white">
              <div
                className={`h-2 rounded-full ${summary.source === 'overall' ? 'bg-sky-400' : summary.source === 'call' ? 'bg-emerald-400' : 'bg-violet-400'}`}
                style={{ width: `${Math.max(4, summary.score)}%` }}
              />
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <SmallStat label="分析数" value={`${summary.analyzedCount}`} />
              <SmallStat label="根拠" value={`${summary.evidenceCount}`} />
            </div>
            <p className="mt-2 truncate text-xs text-gray-500">
              {coverageLabel(summary.dataCoverage)} / {summary.topTraitLabel ?? 'データ待ち'}
            </p>
          </div>
        ))}
      </div>
    </section>
  )
}

function InsightCard({
  strengths,
  improvements,
  callProfile,
  lineProfile
}: {
  strengths: ManagerFeedbackTraitScore[]
  improvements: ManagerFeedbackTraitScore[]
  callProfile: ManagerFeedbackProfile
  lineProfile: ManagerFeedbackProfile
}) {
  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">改善の入口</h2>
          <p className="mt-1 text-xs text-gray-500">強みを残しながら、次に伸ばす軸を見ます。</p>
        </div>
        <HeartHandshake className="h-4 w-4 text-gray-400" />
      </div>
      <div className="space-y-4">
        <InsightList title="強み候補" traits={strengths} empty="強み候補はデータ蓄積後に表示されます。" tone="emerald" />
        <InsightList title="伸ばす余地" traits={improvements} empty="改善候補はデータ蓄積後に表示されます。" tone="amber" />
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
          <SourceHint title="電話で見ること" profile={callProfile} empty="通話ログが増えると、深い相談・目標設定・次回接点が見えます。" />
          <SourceHint title="LINEで見ること" profile={lineProfile} empty="LINE履歴が増えると、日常対応・返信速度・リスク兆候が見えます。" />
        </div>
      </div>
    </section>
  )
}

function InsightList({
  title,
  traits,
  empty,
  tone
}: {
  title: string
  traits: ManagerFeedbackTraitScore[]
  empty: string
  tone: 'emerald' | 'amber'
}) {
  return (
    <div>
      <h3 className="text-xs font-semibold text-gray-700">{title}</h3>
      {traits.length > 0 ? (
        <div className="mt-2 space-y-2">
          {traits.map((trait) => (
            <div key={trait.key} className={`rounded-lg px-3 py-2 ${tone === 'emerald' ? 'bg-emerald-50' : 'bg-amber-50'}`}>
              <div className="flex items-center justify-between gap-3">
                <p className={`text-sm font-medium ${tone === 'emerald' ? 'text-emerald-900' : 'text-amber-900'}`}>
                  {trait.label}
                </p>
                <span className={`text-xs tabular-nums ${tone === 'emerald' ? 'text-emerald-700' : 'text-amber-700'}`}>
                  {trait.score}
                </span>
              </div>
              <p className={`mt-1 text-xs leading-5 ${tone === 'emerald' ? 'text-emerald-700' : 'text-amber-700'}`}>
                {trait.description}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-2 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-400">{empty}</p>
      )}
    </div>
  )
}

function SourceHint({
  title,
  profile,
  empty
}: {
  title: string
  profile: ManagerFeedbackProfile
  empty: string
}) {
  const top = profile.primaryStrengths[0]
  return (
    <div className="rounded-lg border border-gray-100 px-3 py-2">
      <p className="text-xs font-semibold text-gray-700">{title}</p>
      <p className="mt-1 text-xs leading-5 text-gray-500">
        {top ? `${top.label}: ${top.description}` : empty}
      </p>
    </div>
  )
}

function SmallStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-white px-2 py-1 ring-1 ring-gray-100">
      <p className="text-[11px] text-gray-400">{label}</p>
      <p className="text-xs font-semibold tabular-nums text-gray-700">{value}</p>
    </div>
  )
}

function coverageLabel(coverage: FeedbackDataCoverage): string {
  switch (coverage) {
    case 'rich':
      return '十分なログ量'
    case 'useful':
      return '分析に使えるログ量'
    case 'thin':
      return '初期分析'
    case 'empty':
      return 'データ待ち'
  }
}

const TONE_CLASS: Record<MetricTone, string> = {
  sky: 'bg-sky-50 text-sky-700',
  emerald: 'bg-emerald-50 text-emerald-700',
  violet: 'bg-violet-50 text-violet-700',
  amber: 'bg-amber-50 text-amber-700'
}
